import { Edit2, AlertTriangle, ArrowRightLeft, Package } from 'lucide-react';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import '../inventory.css';
import {
  fetchWarehouseById,
  formatCurrency
} from '../../../services/warehouseService';
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TOAST_KEY = 'warehouse_list_toast';

function statusLabel(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  return status;
}

function dispatchMethodLabel(method) {
  if (method === 'FIFO') return i18n.t('inventory.fifoLabel');
  if (method === 'FEFO') return i18n.t('inventory.fefoLabel');
  if (method === 'Both') return i18n.t('onboarding.both');
  return method || i18n.t('inventory.fifoLabel');
}

export default function WarehouseDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    const storedToast = sessionStorage.getItem(TOAST_KEY);
    if (storedToast) {
      setToastMessage(storedToast);
      sessionStorage.removeItem(TOAST_KEY);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!id) {
        navigate('/IMS/inventory/locations');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchWarehouseById(id);
        if (cancelled) return;
        if (!data) {
          navigate('/IMS/inventory/locations');
          return;
        }
        setWarehouse(data);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || i18n.t('inventory.failedToLoadWarehouse'));
        setWarehouse(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) {
    return (
      <AppLayout>
        <div className="inventory-module" style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
          <Trans i18nKey="inventory.loadingWarehouse" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="inventory-module" style={{ padding: '32px 20px' }}>
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '16px 20px',
              color: '#991b1b',
              fontSize: '13px',
            }}
          >
            <p style={{ margin: '0 0 12px 0' }}>{error}</p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/IMS/inventory/locations')}
            >
              <Trans i18nKey="inventory.backToWarehouses" />
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!warehouse) return null;

  const isInactive = warehouse.status === 'Inactive';
  const isPrimary = warehouse.primaryHub === 'Yes';
  const fullAddress = [warehouse.addressLine1, warehouse.addressLine2, warehouse.city, warehouse.state, warehouse.pincode]
    .filter(Boolean)
    .join(', ');

  const hasStockItems = warehouse.stockItems && warehouse.stockItems.length > 0;

  return (
    <AppLayout>
      <div className="inventory-module">
        {toastMessage && (
          <LeaveActionToast
            message={toastMessage}
            onClose={() => setToastMessage(null)}
          />
        )}

        {/* BACK NAVIGATION */}
        <div style={{ marginBottom: '16px' }}>
          <PageBackLink to="/IMS/inventory/locations" />
        </div>

        {/* HEADER SECTION */}
        <div
          className="content-header page-header-responsive"
          style={{ alignItems: 'flex-start', marginBottom: '8px' }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h1 className="page-title" style={{ margin: 0, textTransform: 'uppercase' }}>
                {warehouse.name}
              </h1>
              <span
                style={{
                  backgroundColor: isInactive ? '#f1f5f9' : '#dcfce7',
                  color: isInactive ? '#64748b' : '#15803d',
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '4px 12px',
                  borderRadius: '16px'
                }}
              >
                {statusLabel(warehouse.status)}
              </span>
              {isPrimary && (
                <span
                  style={{
                    backgroundColor: '#e0e7ff',
                    color: '#3730a3',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 12px',
                    borderRadius: '16px'
                  }}
                >
                  <Trans i18nKey="inventory.hub" />
                </span>
              )}
            </div>

            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
              <strong style={{ color: '#334155' }}>{warehouse.code}</strong> · {warehouse.type || i18n.t('inventory.emDash')} · {fullAddress || i18n.t('inventory.emDash')} ·{' '}
              <strong style={{ color: '#334155' }}><Trans i18nKey="inventory.managerLabel" /></strong> {warehouse.manager || i18n.t('inventory.unassigned')} ·{' '}
              <strong style={{ color: '#334155' }}><Trans i18nKey="inventory.dispatchLabel" /></strong> {dispatchMethodLabel(warehouse.fifoFefoSupport)}
            </p>
          </div>

          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/IMS/inventory/locations/${warehouse.id}/edit`)}
            style={{ borderRadius: '6px', padding: '8px 18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Edit2 size={14} />
            <Trans i18nKey="common.edit" />
          </button>
        </div>

        {isInactive && (
          <div
            style={{
              backgroundColor: '#fff7ed',
              border: '1px solid #ffedd5',
              borderRadius: '12px',
              padding: '14px 18px',
              marginTop: '16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#c2410c'
            }}
          >
            <AlertTriangle size={20} style={{ flexShrink: 0, color: '#f97316' }} />
            <div style={{ fontSize: '13px', lineHeight: '1.45' }}>
              <strong><Trans i18nKey="inventory.statusInactiveBanner" /></strong>{' '}
              <Trans i18nKey="inventory.warehouseInactiveHint" />
            </div>
          </div>
        )}

        <div
          className="grid-4-cols"
          style={{ marginTop: '20px', marginBottom: '28px', gap: '16px' }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Trans i18nKey="inventory.skusHeldColumn" />
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginTop: '6px' }}>
              {warehouse.skusHeld || 0}
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Trans i18nKey="inventory.totalUnitsColumn" />
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginTop: '6px' }}>
              {(warehouse.totalUnits || 0).toLocaleString('en-IN')}
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Trans i18nKey="inventory.reservedUnitsColumn" />
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginTop: '6px' }}>
              {(warehouse.reservedUnits || 0).toLocaleString('en-IN')}
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Trans i18nKey="inventory.stockValueColumn" />
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', marginTop: '6px' }}>
              {formatCurrency(warehouse.stockValue || 0)}
            </div>
          </div>
        </div>

        <div
          className="table-section"
          style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
        >
          <div
            style={{
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#ffffff'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
              <Trans i18nKey="inventory.stockHeldHere" />
            </h3>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => alert(`Stock Position link clicked for warehouse: ${warehouse.name}`)}
              style={{
                borderRadius: '6px',
                padding: '6px 16px',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <ArrowRightLeft size={14} />
              <Trans i18nKey="inventory.movementTransferOut" />
            </button>
          </div>

          <div className="table-wrapper">
            <table className="leads-table">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <Trans i18nKey="inventory.productColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.totalColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.reservedColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.defectiveColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.maintenanceReserveColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.availableColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.stockValueColumn" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {hasStockItems ? (
                  warehouse.stockItems.map((item) => (
                    <tr key={item.id}>
                      <td style={{ padding: '14px 20px', verticalAlign: 'middle' }}>
                        <div>
                          <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px' }}>
                            {item.productName}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                            {item.sku}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', color: '#64748b', fontSize: '14px' }}>
                        {item.total}
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', color: '#64748b', fontSize: '14px' }}>
                        {item.reserved}
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', color: item.defective > 0 ? '#ef4444' : '#64748b', fontSize: '14px' }}>
                        {item.defective}
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', color: '#64748b', fontSize: '14px' }}>
                        {item.maintenanceReserve || 0}
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0f172a', fontSize: '14px' }}>
                        {item.available}
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right', color: '#0f172a', fontSize: '14px', fontWeight: '500' }}>
                        {formatCurrency(item.stockValue || 0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '48px 20px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <Package size={36} style={{ color: '#94a3b8' }} />
                        <h4 style={{ margin: 0, color: '#334155', fontWeight: '600' }}>
                          <Trans i18nKey="inventory.warehouseHoldsNoStock" />
                        </h4>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                          <Trans i18nKey="inventory.warehouseNoStockHint" />
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
